import { NetworkStatus, useLazyQuery, useQuery } from '@apollo/client';
import { Box, Button, Heading, Icon, Text } from '@bechtel/gds';
import { CONSUMABLE_TYPES, useAppUser, useUpdateToolUserFavorites } from '@bechtel/ht-clients-common';
import {
  FilterConditionInput,
  FilterInput,
  OperatorType,
  SearchInput,
  SearchToolCatalogResult,
} from '@bechtel/ht-types';
import debounce from 'lodash/debounce';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import LoadingIndicator from '~/components/loading-indicator';
import { GET_TOOL_CATALOG_COUNT, SEARCH_TOOL_CATALOGS } from '~/gql/tool-catalog';
import { ToolCatalogListStyled } from './styled';
import { ToolCatalogItem } from './tool-catalog-item';
import { ToolCatalogListProps } from './types';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
//@ts-ignore

export const ToolCatalogList: React.FC<ToolCatalogListProps> = ({
  onToolCatalogItemEdit,
  toolOrderCatalogItems,
  toolCategory,
  searchInput,
  setToolFavoriteCount,
  showFavoriteIcon = true,
}) => {
  const { appUser } = useAppUser();
  const userFavoritesRef = useRef<{ newFavorites: string[]; removedFavorites: string[] }>({
    newFavorites: [],
    removedFavorites: [],
  });
  const filterInput: FilterInput = useMemo(() => {
    const and: FilterConditionInput[] = [];
    and.push({ field: 'isActive', operator: OperatorType.EQUAL, value: '1' }); //TODO: Introduce Enum for InventoryType
    if (searchInput.consumables) {
      and.push({ field: 'inventoryType', operator: OperatorType.IN, values: CONSUMABLE_TYPES }); //TODO: Introduce Enum for InventoryType
    }
    if (searchInput.tools) {
      and.push({ field: 'inventoryType', operator: OperatorType.IN, values: ['Individual'] });
    }
    if (searchInput.filterByFavorites && appUser?.id) {
      and.push({
        field: 'toolFavorites.userId',
        operator: OperatorType.EQUAL,
        value: appUser?.id,
      });
      and.push({
        field: 'toolFavorites.projectId',
        operator: OperatorType.EQUAL,
        value: appUser?.defaultProjectId,
      });
    }
    if (toolCategory.id && toolCategory.id !== 'All') {
      and.push({ field: 'toolCategory.name', operator: OperatorType.EQUAL, value: toolCategory.name });
    }

    const filter: FilterInput = {
      or: [
        { field: 'name', operator: OperatorType.LIKE, value: searchInput.searchText },
        { field: 'bscnPartNumber', operator: OperatorType.LIKE, value: searchInput.searchText },
      ],
      and: and,
    };
    return { ...filter };
  }, [
    searchInput.searchText,
    searchInput.consumables,
    searchInput.filterByFavorites,
    searchInput.tools,
    toolCategory?.id,
    toolOrderCatalogItems,
  ]);

  const [getCatalogList, { loading, error, refetch, networkStatus, data, fetchMore }] = useLazyQuery<
    { searchToolCatalogs: SearchToolCatalogResult },
    { searchInput: SearchInput }
  >(SEARCH_TOOL_CATALOGS, {
    variables: {
      searchInput: {
        filterInput: filterInput,
        sortInput: [],
        paginationInput: {
          limit: 20,
          offset: 0,
        },
      },
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });

  const {
    loading: totalLoading,
    error: totalError,
    data: totalData,
  } = useQuery<{ toolCatalogsCountForSearch: number }, { searchInput: SearchInput }>(GET_TOOL_CATALOG_COUNT, {
    variables: {
      searchInput: {
        filterInput: filterInput,
        sortInput: [],
      },
    },
  });

  useEffect(() => {
    getCatalogList();
  }, [
    toolCategory.id,
    searchInput.filterByFavorites,
    searchInput.searchText,
    searchInput.consumables,
    searchInput.tools,
    getCatalogList,
  ]);

  const getToolfavorites = () => {
    const filteredArray = data?.searchToolCatalogs?.items?.filter((tool) => {
      if (tool?.isFavorite) {
        return tool;
      }
    });
    return filteredArray;
  };

  const toolCatalogList = searchInput.filterByFavorites
    ? getToolfavorites() || []
    : data?.searchToolCatalogs?.items || [];

  const {
    updateToolUserFavorites,
    data: toolFavoritesData,
    loading: toolFavoritesLoading,
    error: errorOnToolFavorites,
  } = useUpdateToolUserFavorites();

  const clearFavoritesRef = () => {
    userFavoritesRef.current = { newFavorites: [], removedFavorites: [] };
  };
  const debouncedUpdateFavorites = useCallback(
    debounce(async (favorites, nonFavorites) => {
      try {
        const input = {
          additions: Array.from(favorites) ?? [],
          deletions: Array.from(nonFavorites) ?? [],
          userId: appUser?.id ?? '',
        };
        await updateToolUserFavorites(input, clearFavoritesRef);
      } catch (error) {
        console.error('Error updating favorites:', error);
      }
    }, 1000),
    []
  );

  const handleFavoriteChange = (toolCatalogId, isFavorite) => {
    // Update the ref based on whether the item is a favorite or not
    const { newFavorites, removedFavorites } = userFavoritesRef.current;

    if (isFavorite) {
      if (!newFavorites.includes(toolCatalogId)) {
        newFavorites.push(toolCatalogId);
      }
    } else {
      if (!removedFavorites.includes(toolCatalogId)) {
        removedFavorites.push(toolCatalogId);
      }
    }

    // Update the ref
    userFavoritesRef.current = { newFavorites, removedFavorites };

    // Call debounced function with updated values
    debouncedUpdateFavorites(newFavorites, removedFavorites);
  };
  if (!loading && error)
    return (
      <Box
        alignItems={{ sm: 'center' }}
        justifyContent={{ sm: 'center' }}
        flexGrow={{ sm: 1 }}
        flexDirection={{ sm: 'column' }}
        p={{ sm: '500' }}
      >
        <Box pb={{ sm: '500' }}>
          <Text>There was a problem loading catalogs. Please click the button below to try again.</Text>
        </Box>
        <Button onClick={() => refetch()}>
          <Box mr={{ sm: '400' }}>
            <Icon icon="undo" />
          </Box>
          Retry
        </Button>
      </Box>
    );

  if (!loading && error) {
    return (
      <Box alignItems={{ sm: 'center' }} justifyContent={{ sm: 'center' }} flexGrow={{ sm: 1 }} css="height: 100%;">
        <div>Error loading tool catalog items</div>
      </Box>
    );
  }
  const handleScroll = (e) => {
    if (!totalData?.toolCatalogsCountForSearch) return;

    if (
      e.target.scrollTop + e.target.clientHeight + 1 >= e.target.scrollHeight &&
      !loading &&
      !totalLoading &&
      toolCatalogList?.length < totalData?.toolCatalogsCountForSearch
    ) {
      fetchMore({
        variables: {
          searchInput: {
            filterInput: filterInput,
            paginationInput: {
              limit: 20,
              offset: toolCatalogList?.length,
            },
            sortInput: [],
          },
        },
      });
    }
  };
  return (
    <div>
      <ToolCatalogListStyled onScroll={handleScroll}>
        <>
          {toolCatalogList &&
            toolCatalogList?.length > 0 &&
            toolCatalogList?.map((toolCatalog: any) => {
              const toolOrderCatalogItem = toolOrderCatalogItems?.find(
                (item) => item.toolCatalog?.id === toolCatalog.id
              );
              return (
                <ToolCatalogItem
                  key={toolCatalog.id}
                  count={toolOrderCatalogItem?.quantity || 0}
                  toolCatalog={toolCatalog}
                  onItemCountChange={(itemCount) => {
                    onToolCatalogItemEdit(toolCatalog, itemCount);
                  }}
                  showFavoriteIcon={showFavoriteIcon}
                  onFavoriteChange={handleFavoriteChange}
                ></ToolCatalogItem>
              );
            })}
          {!loading && toolCatalogList && toolCatalogList?.length <= 0 && (
            <Box
              alignItems={{ sm: 'center' }}
              justifyContent={{ sm: 'center' }}
              flexGrow={{ sm: 1 }}
              p={{ sm: '500' }}
              css="height: 100%;"
            >
              <Heading size="500" weight="500" color="grey.400">
                No Catalogs found.
              </Heading>
            </Box>
          )}
          {(networkStatus === NetworkStatus.refetch || loading) && <LoadingIndicator></LoadingIndicator>}
        </>
      </ToolCatalogListStyled>
    </div>
  );
};
